import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useUserAuth } from "../../../contexts/UserAuthContext";
import { useToast } from "../../../contexts/ToastContext";
import {
  useReviewsQuery,
  useAnswersQuery,
  useCommentsQuery,
  useNotificationsQuery,
} from "../../../hooks/profile/queries";
import ReviewsView from "../../../components/profile/views/ReviewsView";
import AnswersView from "../../../components/profile/views/AnswersView";
import CommentsView from "../../../components/profile/views/CommentsView";
import FollowRequestsView from "../../../components/profile/views/FollowRequestsView";
import MessagesPanel from "../../../components/profile/MessagesPanel";
import styles from "../../../styles/pages/profile/SharedProfile.module.css";
import api from "../../../utils/api/axiosInstance";
import { useMutation, useQueryClient } from "@tanstack/react-query";

/**
 * Profile community hub — messages, follow requests, reviews, etc.
 * Route: `/user/:userId/messages` (and siblings), not the main `/community` feed.
 */
const CommunityPage = ({ forcedSub }) => {
  const { userId, sub: routeSub } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUserAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const currentUserId = user?.id || user?._id;

  const activeTab = forcedSub || routeSub || "messages";
  const profileBase = userId
    ? `/user/${userId}`
    : currentUserId
      ? `/user/${currentUserId}`
      : "/login";

  // Legacy: ?tab=messages
  React.useEffect(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    if (tab === "messages" && activeTab !== "messages" && userId) {
      navigate(`${profileBase}/messages`, {
        replace: true,
        state: location.state,
      });
    }
  }, [location.search, location.state, activeTab, navigate, profileBase, userId]);

  // Data hooks - only active when needed
  const {
    data: reviews = [],
    isLoading: reviewsLoading,
    isError: reviewsError,
    refetch: refetchReviews,
  } = useReviewsQuery(currentUserId, activeTab === "reviews");
  const {
    data: answers = [],
    isLoading: answersLoading,
    isError: answersError,
    refetch: refetchAnswers,
  } = useAnswersQuery(currentUserId, activeTab === "answers");
  const {
    data: comments = [],
    isLoading: commentsLoading,
    isError: commentsError,
    refetch: refetchComments,
  } = useCommentsQuery(currentUserId, activeTab === "comments");
  const {
    data: notificationData,
    isLoading: notificationsLoading,
    refetch: refetchNotifications,
  } = useNotificationsQuery(currentUserId, activeTab === "requests");

  const followRequests = notificationData?.followRequests || [];

  // Mutations for follow requests
  const acceptRequestMutation = useMutation({
    mutationFn: (requestId) =>
      api.post(`/users/me/follow-requests/${requestId}/accept`),
    onSuccess: () => {
      queryClient.invalidateQueries(["notifications"]);
      queryClient.invalidateQueries(["socialStats"]);
    },
  });

  const rejectRequestMutation = useMutation({
    mutationFn: (requestId) =>
      api.post(`/users/me/follow-requests/${requestId}/reject`),
    onSuccess: () => {
      queryClient.invalidateQueries(["notifications"]);
    },
  });

  const switchTab = (tabName) => {
    navigate(`${profileBase}/${tabName}`);
  };

  const renderContent = () => {
    switch (activeTab) {
      case "messages":
        return (
          <div className={styles.messagesSection}>
            <MessagesPanel
              initialStartConversationWith={location.state?.startConversationWith}
              initialMessage={location.state?.initialMessage}
              scope="community"
              variant="embedded"
            />
          </div>
        );
      case "requests":
        return (
          <FollowRequestsView
            followRequests={followRequests}
            loadingFollowRequests={notificationsLoading}
            onRefresh={refetchNotifications}
            onAccept={(id) => acceptRequestMutation.mutate(id)}
            onReject={(id) => rejectRequestMutation.mutate(id)}
            showToast={showToast}
          />
        );
      case "reviews":
        return (
          <ReviewsView
            reviews={reviews}
            loading={reviewsLoading}
            error={reviewsError ? "Failed to load reviews." : null}
            onRefresh={refetchReviews}
            showToast={showToast}
            currentTab="community"
          />
        );
      case "answers":
        return (
          <AnswersView
            answers={answers}
            loading={answersLoading}
            error={answersError ? "Failed to load answers." : null}
            onRefresh={refetchAnswers}
            showToast={showToast}
            currentTab="community"
          />
        );
      case "comments":
        return (
          <CommentsView
            comments={comments}
            loading={commentsLoading}
            error={commentsError ? "Failed to load comments." : null}
            onRefresh={refetchComments}
            showToast={showToast}
            currentTab="community"
          />
        );
      case "blocked":
        // Redirect to settings page where blocked users management is now located
        navigate(`${profileBase}/settings`);
        return null;
      default:
        return null;
    }
  };

  return (
    <div className={styles.communityPage}>
      {/* Content area */}
      {renderContent()}
    </div>
  );
};

export default CommunityPage;
